import React from "react";
import { Box, Typography, Stack, Chip, Divider } from "@mui/material";
import {
  AccessTime,
  SdCardAlertOutlined,
  TextSnippetOutlined,
  QuestionMark,
} from "@mui/icons-material";
import QuestionContent from "../QuestionContent";

/**
 * Question Panel Component - Displays question information, description, sample I/O, limits, and languages
 */
const QuestionPanel = ({
  questionTitle,
  questionContent,
  sampleInput,
  sampleOutput,
  timeLimit,
  memoryLimit,
  maxCodeSize,
  supportedLanguages,
  questionContainerStyles,
  isMobile = false,
  questionIndex,
}) => {
  return (
    <Box className="question-container" sx={questionContainerStyles}>
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: { base: "16px", sm: "18px" },
            fontWeight: 600,
            color: "#1a1a1a",
            display: "flex",
            gap: 1,
            alignItems: "flex-start",
          }}
        >
          <span>{`Q${(questionIndex ?? 0) + 1}.`}</span>
          <span>{questionTitle}</span>
        </Typography>
      </Box>
      <Divider sx={{ mb: 2, borderColor: "#8e8e91" }} />
      {/* Problem Description */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "#4a4a4a",
            mb: 1,
          }}
        >
          Problem Description
        </Typography>
        <Box
          sx={{
            fontSize: "14px",
            lineHeight: 1.6,
            color: "#333",
            "& img": { maxWidth: "100%", height: "auto", borderRadius: "4px" },
          }}
        >
          {questionContent}
        </Box>
      </Box>

      {/* Sample Input/Output */}
      {sampleInput && sampleOutput && (
        <Box sx={{ mb: 1, width: "100%", overflow: "hidden" }}>
          <Stack
            direction={{ base: "column", md: "row" }}
            spacing={1}
            textAlign="left"
            sx={{ width: "100%" }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                border: "1px solid #e0e0e0",
                borderRadius: 3,
                p: 2,
                width: "100%",
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <Typography
                sx={{
                  fontSize: { base: "14px", sm: "16px" },
                  fontWeight: 500,
                }}
              >
                Sample Input
              </Typography>
              <Box
                sx={{
                  fontFamily: "monospace",
                  backgroundColor: "#D5F9F1",
                  p: { base: 1.5, sm: 2 },
                  borderRadius: 1,
                  fontSize: { base: "13px", sm: "14px" },
                  fontWeight: 400,
                  border: "1px solid #00C49A",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  overflowX: "auto",
                  overflowY: "auto",
                  maxHeight: { base: "150px", sm: "200px" },
                  minWidth: 0,
                  width: "100%",
                  boxSizing: "border-box",
                }}
                component="pre"
              >
                {sampleInput}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                border: "1px solid #e0e0e0",
                borderRadius: 3,
                p: 2,
                width: "100%",
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <Typography
                sx={{
                  fontSize: { base: "14px", sm: "16px" },
                  fontWeight: 500,
                }}
              >
                Sample Output
              </Typography>
              <Box
                sx={{
                  fontFamily: "monospace",
                  backgroundColor: "#D5F9F1",
                  p: { base: 1.5, sm: 2 },
                  borderRadius: 1,
                  fontSize: { base: "13px", sm: "14px" },
                  fontWeight: 400,
                  border: "1px solid #00C49A",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  overflowX: "auto",
                  overflowY: "auto",
                  maxHeight: { base: "150px", sm: "200px" },
                  minWidth: 0,
                  width: "100%",
                  boxSizing: "border-box",
                }}
                component="pre"
              >
                {sampleOutput}
              </Box>
            </Box>
          </Stack>
        </Box>
      )}

      {/* Limits & Languages Row */}
      {/* Limits & Languages Row */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #f0f0f0",
          display: "grid",
          gridTemplateColumns: {
            base: "repeat(auto-fit, minmax(100px, 1fr))",
            sm: "initial",
          },
          gridAutoFlow: { sm: "column" },
          gap: { base: 2, sm: 4 },
          alignItems: "center",
        }}
      >
        {/* Time Limit */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AccessTime sx={{ fontSize: "18px", color: "#666" }} />
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              lineHeight={1}
              mb={0.5}
            >
              Time
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {timeLimit}s
            </Typography>
          </Box>
        </Box>

        {/* Divider (Desktop Only) */}
        <Box
          sx={{
            display: { base: "none", sm: "block" },
            width: "1px",
            bgcolor: "#e0e0e0",
            height: "24px",
          }}
        />

        {/* Memory Limit */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SdCardAlertOutlined sx={{ fontSize: "18px", color: "#666" }} />
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              lineHeight={1}
              mb={0.5}
            >
              Memory
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {memoryLimit} MB
            </Typography>
          </Box>
        </Box>

        {/* Divider (Desktop Only) */}
        <Box
          sx={{
            display: { base: "none", sm: "block" },
            width: "1px",
            bgcolor: "#e0e0e0",
            height: "24px",
          }}
        />

        {/* Code Size */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextSnippetOutlined sx={{ fontSize: "18px", color: "#666" }} />
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              lineHeight={1}
              mb={0.5}
            >
              Size
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {maxCodeSize} KB
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Available Languages - Moved below limits or integrated if prefer small */}
      {/* Keeping separate but cleaner */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "#4a4a4a",
            mb: 1,
          }}
        >
          Supported Languages
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {supportedLanguages.map((language) => (
            <Chip
              key={language.languageId}
              label={language.languageName}
              size="small"
              sx={{
                bgcolor: "#e3f2fd",
                color: "#1565c0",
                fontSize: "12px",
                height: "24px",
                fontWeight: 500,
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default QuestionPanel;
